import React, {useEffect, useState} from 'react'
import {Modal, Spinner} from 'react-bootstrap'
import axios from 'axios'
import {useAuth} from '../../../modules/auth'
import {FaExclamationTriangle, FaFileAlt} from 'react-icons/fa'
import {HiOutlineExternalLink} from 'react-icons/hi'

interface DocumentPreviewModalProps {
  claimNumber: string
  fileName: string
  label: string
  isOpen: boolean
  onClose: () => void
}

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  claimNumber,
  fileName,
  label,
  isOpen,
  onClose,
}) => {
  const {auth} = useAuth()
  const user = useAuth()
  const [fileUrl, setFileUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchFileUrl = async () => {
      if (!isOpen || !fileName || !claimNumber) return

      setIsLoading(true)
      setError(null)
      try {
        const apiUrl = `${
          process.env.REACT_APP_API_CLAIM_URL
        }/api/Claims/GetFile?claimNumber=${claimNumber}&fileName=${encodeURIComponent(fileName)}`
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
        })

        if (response.data.isSuccess && response.data.data?.url) {
          setFileUrl(response.data.data.url)
        } else {
          setError('Failed to get preview URL')
        }
      } catch (err) {
        console.error('Error fetching preview URL:', err)
        setError('Error loading document preview')
      } finally {
        setIsLoading(false)
      }
    }

    if (isOpen) {
      fetchFileUrl()
    } else {
      // Clear states when closed to avoid showing old content on next open
      setFileUrl(null)
      setError(null)
    }
  }, [isOpen, claimNumber, fileName, auth?.token, user.currentUser?.token])

  const renderContent = () => {
    if (isLoading) {
      return (
        <div
          className='d-flex flex-column justify-content-center align-items-center'
          style={{minHeight: '400px', background: '#f8f9fa'}}
        >
          <Spinner animation='border' variant='primary' style={{width: '3rem', height: '3rem'}} />
          <p className='mt-3 text-muted fw-bold'>Fetching secure preview...</p>
        </div>
      )
    }

    if (error) {
      return (
        <div className='text-center p-5' style={{minHeight: '400px'}}>
          <div className='mb-3'>
            <FaExclamationTriangle className='text-danger' style={{fontSize: '3rem'}} />
          </div>
          <h5 className='text-danger mb-3'>{error}</h5>
          <p className='text-muted mb-4'>
            We couldn't load the preview for this document. You can try opening it in a new tab
            instead.
          </p>
          <button className='btn btn-outline-primary btn-sm px-4' onClick={() => onClose()}>
            Close
          </button>
        </div>
      )
    }

    if (!fileUrl) return null

    const fileExtension = fileName.split('.').pop()?.toLowerCase()

    if (fileExtension === 'pdf') {
      return (
        <div style={{background: '#525659', padding: '10px', height: '725px'}}>
          <iframe
            src={`${fileUrl}#toolbar=0`}
            title='PDF Preview'
            width='100%'
            height='100%'
            style={{border: 'none', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'}}
          />
        </div>
      )
    }

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension || '')) {
      return (
        <div
          className='text-center d-flex align-items-center justify-content-center'
          style={{minHeight: '400px', background: '#f1f3f5', padding: '20px'}}
        >
          <img
            src={fileUrl}
            alt='Document Preview'
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: '4px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}
          />
        </div>
      )
    }

    return (
      <div
        className='text-center p-5 d-flex flex-column align-items-center justify-content-center'
        style={{minHeight: '400px'}}
      >
        <div className='mb-4'>
          <FaFileAlt className='text-primary' style={{fontSize: '4rem'}} />
        </div>
        <h5>Preview not available</h5>
        <p className='text-muted mb-4'>
          This file type ({fileExtension?.toUpperCase()}) cannot be previewed directly in the
          browser.
        </p>
        <a
          href={fileUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='btn btn-primary px-5 py-2 shadow-sm'
        >
          Download / Open in New Tab
        </a>
      </div>
    )
  }

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size='xl'
      centered
      contentClassName='preview-modal-content'
    >
      <Modal.Header closeButton className='border-0 pb-0'>
        <Modal.Title className='fs-4 fw-bold text-dark'>{label || 'Document Preview'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className='p-0 mt-3'>{renderContent()}</Modal.Body>
      <Modal.Footer className='border-0 pt-3'>
        <button className='btn btn-light fw-bold px-4' onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
      <style>{`
        .preview-modal-content {
          border-radius: 12px;
          overflow: hidden;
          border: none;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }
      `}</style>
    </Modal>
  )
}

export default DocumentPreviewModal
